import { supabase } from "@/integrations/supabase/client";

export interface CustomTemplate {
  id: string;
  user_id: string;
  template_name: string;
  original_filename: string;
  template_file_path: string;
  template_file_url: string | null;
  field_mappings: Array<{
    field: string;
    placeholder: string;
    context: string;
  }>;
  style_metadata: {
    hasStyles: boolean;
    hasTheme: boolean;
    hasNumbering: boolean;
    documentStructure: {
      paragraphCount: number;
      sections: number;
    };
    extractedAt: string;
  };
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export class CustomTemplateService {
  static async generateTemplate(file: File, templateName: string): Promise<CustomTemplate> {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('templateName', templateName);

      const { data, error } = await supabase.functions.invoke('generate-custom-docmosis-template', {
        body: formData
      });

      if (error) {
        throw new Error(error.message || 'Failed to generate template');
      }

      if (!data.success) {
        throw new Error(data.error || 'Template generation failed');
      }

      return data.template;
    } catch (error) {
      console.error('Error generating custom template:', error);
      throw new Error(
        error instanceof Error 
          ? error.message 
          : 'Failed to generate custom template'
      );
    }
  }

  static async getUserTemplates(): Promise<CustomTemplate[]> {
    try {
      const { data, error } = await supabase
        .from('custom_resume_templates')
        .select('*')
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      if (error) {
        throw new Error(error.message);
      }

      return (data || []) as CustomTemplate[];
    } catch (error) {
      console.error('Error fetching user templates:', error);
      throw new Error(
        error instanceof Error 
          ? error.message 
          : 'Failed to fetch templates'
      );
    }
  }

  static async getTemplateById(templateId: string): Promise<CustomTemplate | null> {
    try {
      const { data, error } = await supabase
        .from('custom_resume_templates')
        .select('*')
        .eq('id', templateId)
        .eq('is_active', true)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          return null; // Template not found
        }
        throw new Error(error.message);
      }

      return data as CustomTemplate;
    } catch (error) {
      console.error('Error fetching template:', error);
      throw new Error(
        error instanceof Error 
          ? error.message 
          : 'Failed to fetch template'
      );
    }
  }

  static async updateTemplate(
    templateId: string, 
    updates: Partial<Pick<CustomTemplate, 'template_name' | 'is_active'>>
  ): Promise<CustomTemplate> {
    try {
      const { data, error } = await supabase
        .from('custom_resume_templates')
        .update(updates)
        .eq('id', templateId)
        .select()
        .single();

      if (error) {
        throw new Error(error.message);
      }

      return data as CustomTemplate;
    } catch (error) {
      console.error('Error updating template:', error);
      throw new Error(
        error instanceof Error 
          ? error.message 
          : 'Failed to update template'
      );
    }
  }

  static async deleteTemplate(templateId: string): Promise<void> {
    try {
      // Soft delete by setting is_active to false
      const { error } = await supabase
        .from('custom_resume_templates')
        .update({ is_active: false })
        .eq('id', templateId);

      if (error) {
        throw new Error(error.message);
      }
    } catch (error) {
      console.error('Error deleting template:', error);
      throw new Error(
        error instanceof Error 
          ? error.message 
          : 'Failed to delete template'
      );
    }
  }

  static async generateResumeFromTemplate(
    templateId: string, 
    resumeData: Record<string, any>
  ): Promise<Blob> {
    try {
      const template = await this.getTemplateById(templateId);
      if (!template) {
        throw new Error('Template not found');
      }

      // Map resume data to template placeholders
      const mappedData = this.mapResumeDataToTemplate(resumeData, template.field_mappings);

      const { data, error } = await supabase.functions.invoke('generate-resume-from-custom-template', {
        body: {
          templateUrl: template.template_file_url,
          resumeData: mappedData
        }
      });

      if (error) {
        throw new Error(error.message || 'Failed to generate resume');
      }

      if (data instanceof Blob) {
        return data;
      }

      // Convert base64 or other formats to blob
      const blob = new Blob([data], {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      });

      return blob;
    } catch (error) {
      console.error('Error generating resume from template:', error);
      throw new Error(
        error instanceof Error 
          ? error.message 
          : 'Failed to generate resume from template'
      );
    }
  }

  private static mapResumeDataToTemplate(
    resumeData: Record<string, any>, 
    fieldMappings: CustomTemplate['field_mappings']
  ): Record<string, any> {
    const mappedData: Record<string, any> = {};

    fieldMappings.forEach(mapping => {
      const { field, placeholder } = mapping;
      
      // Map common resume fields
      switch (field) {
        case 'name':
          mappedData[placeholder] = resumeData.personalInfo?.fullName || resumeData.name || '';
          break;
        case 'email':
          mappedData[placeholder] = resumeData.personalInfo?.email || resumeData.email || '';
          break;
        case 'phone':
          mappedData[placeholder] = resumeData.personalInfo?.phone || resumeData.phone || '';
          break;
        case 'address':
          mappedData[placeholder] = resumeData.personalInfo?.address || resumeData.address || '';
          break;
        case 'city':
          mappedData[placeholder] = resumeData.personalInfo?.city || resumeData.city || '';
          break;
        case 'linkedin':
          mappedData[placeholder] = resumeData.personalInfo?.linkedin || resumeData.linkedin || '';
          break;
        case 'jobTitle':
          mappedData[placeholder] = resumeData.personalInfo?.title || resumeData.jobTitle || '';
          break;
        default:
          // Try to find the field in the resume data
          mappedData[placeholder] = resumeData[field] || '';
      }
    });

    return mappedData;
  }

  static downloadBlob(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
}